/**
 * Ein Aufenthalt in einer Monatswoche: am Desktop als Balken mit Quelle, Nächten und geplanter
 * Abreise, am Handy als farbiger Streifen am Zellboden. Vergangene Aufenthalte gedämpft.
 */
import { CalendarClock, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { bookingSourceLabel } from '../calendar/BookingBar';
import { dayAndMonth } from '../calendar/calendarFormat';
import type { MonthWeekBar } from './monthModel';

const COLOR = {
  BookingCom: 'bg-navy text-white',
  Airbnb: 'bg-sealwax text-white',
  Manual: 'bg-amber text-ink',
  Other: 'bg-ink-soft text-white',
} as const;

export function MonthBar({
  placed,
  today,
  compact,
}: {
  placed: MonthWeekBar;
  today: string;
  compact: boolean;
}) {
  const { bar } = placed;
  const planned = bar.earlyDeparturePlan?.plannedDepartureDate;
  const period = `${dayAndMonth(bar.start)} – ${dayAndMonth(bar.endExclusive)}`;
  const label = `${bookingSourceLabel(bar.source)}, ${period}${planned ? `, geplante Abreise ${dayAndMonth(planned)}` : ''}`;
  const place = {
    left: `calc(${(placed.start / 7) * 100}% + 2px)`,
    width: `calc(${((placed.end - placed.start) / 7) * 100}% - 4px)`,
  };
  if (compact) {
    return (
      <span
        role="img"
        aria-label={label}
        className={cn(
          'pointer-events-none absolute bottom-1.5 h-1 rounded-full',
          COLOR[bar.source],
          bar.endExclusive <= today && 'opacity-45',
        )}
        style={place}
      />
    );
  }
  return (
    <div
      aria-label={label}
      title={label}
      className={cn(
        'pointer-events-none absolute top-20 flex h-[26px] items-center gap-1.5 overflow-hidden whitespace-nowrap px-2.5 text-xs font-bold',
        COLOR[bar.source],
        !placed.cutStart && 'rounded-l-[9px]',
        !placed.cutEnd && 'rounded-r-[9px]',
        bar.endExclusive <= today && 'opacity-45',
      )}
      style={place}
    >
      {placed.cutStart && <ChevronLeft size={12} aria-hidden className="shrink-0" />}
      <span className="truncate">
        {bookingSourceLabel(bar.source)}
        {!bar.clippedStart && !bar.clippedEnd && ` · ${bar.nights} N.`}
      </span>
      {planned && (
        <span className="inline-flex shrink-0 items-center gap-1">
          <CalendarClock size={12} aria-hidden /> Plan {dayAndMonth(planned)}
        </span>
      )}
      {placed.cutEnd && <ChevronRight size={12} aria-hidden className="ml-auto shrink-0" />}
    </div>
  );
}
